import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Activity, AlertTriangle, Briefcase, ClipboardList, FileWarning, IdCard,
  Plus, Radio, TrendingUp, UserCheck, Users, Wallet, ArrowUpRight, GraduationCap,
} from "lucide-react";

/** Executive Brief — top-of-fold cross-module cockpit surfaced above Strategic + Ops.
 *  Consolidates dockets, HRM, band-registry, and reporting KPIs, adds a Quick Actions
 *  dock, and lists past-deadline items across every module.
 */

const CRIT = "#ef5b6b";
const WARN = "#d4b876";
const OK   = "#89b4fa";
const MUTE = "#8C8C8C";

const currency = (n) => (n == null ? "—" : "$" + Math.round(n).toLocaleString());

export default function CommandExecutiveBrief({ onNewDocket }) {
  const [brief, setBrief] = useState(null);

  const load = () => api.get("/command/executive-brief").then(r => setBrief(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  if (!brief) {
    return <div className="border-y border-white/10 py-6 mb-8 font-mono text-xs text-white/40">Compiling executive brief…</div>;
  }

  const k = brief.kpis;
  const burnColor = k.budget_burn_pct > 90 ? CRIT : k.budget_burn_pct > 75 ? WARN : OK;

  return (
    <section className="mb-10" data-testid="executive-brief">
      {/* Section header */}
      <div className="flex items-center gap-3 mb-4">
        <span className="inline-block w-1.5 h-1.5" style={{ background: OK }} />
        <span className="label-mono" style={{ color: OK }}>Executive brief</span>
        <span className="font-mono text-[10px] text-white/40 hidden sm:inline">
          cross-module cockpit · refresh 30s · {new Date(brief.generated_at).toLocaleTimeString()}
        </span>
      </div>

      {/* KPI grid — 4 domains stacked in one strip */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <DomainCard
          title="Operations"
          icon={Activity}
          testid="brief-operations"
          rows={[
            { label: "Open dockets", value: k.dockets_open, link: null },
            { label: "Overdue", value: k.dockets_overdue, alert: k.dockets_overdue > 0 },
            { label: "Critical", value: k.dockets_critical, alert: k.dockets_critical > 0 },
          ]}
          footer={{
            label: "Burn rate",
            value: `${k.budget_burn_pct}%`,
            subtext: `${currency(k.budget_spent)} of ${currency(k.budget_allocated)}`,
            pct: k.budget_burn_pct,
            color: burnColor,
            icon: Wallet,
          }}
        />

        <DomainCard
          title="Human Resources"
          icon={Users}
          testid="brief-hrm"
          rows={[
            { label: "Active staff", value: k.employees_active },
            { label: "Open postings", value: k.postings_open, link: "/portal/hrm" },
            { label: "In selection", value: k.postings_in_selection, link: "/portal/hrm" },
            { label: "New apps · 7d", value: k.applications_new_7d, link: "/portal/hrm" },
          ]}
          footer={
            k.probation_end_30d > 0 || k.onboarding_in_progress > 0 || k.postings_past_deadline > 0 ? {
              label: "Attention",
              value: k.probation_end_30d + k.onboarding_in_progress + k.postings_past_deadline,
              subtext: [
                k.postings_past_deadline > 0 && `${k.postings_past_deadline} posting past-due`,
                k.onboarding_in_progress > 0 && `${k.onboarding_in_progress} onboarding`,
                k.probation_end_30d > 0 && `${k.probation_end_30d} probation ending`,
              ].filter(Boolean).join(" · "),
              color: WARN, icon: GraduationCap,
            } : null
          }
        />

        <DomainCard
          title="Band Registry"
          icon={IdCard}
          testid="brief-registry"
          rows={[
            { label: "Pending verification", value: k.registry_pending, alert: k.registry_pending > 0 },
          ]}
          footer={
            brief.registry_oldest_pending?.length > 0 ? {
              label: "Oldest waiting",
              value: brief.registry_oldest_pending[0]?.name || "—",
              subtext: brief.registry_oldest_pending[0]?.created_at
                ? new Date(brief.registry_oldest_pending[0].created_at).toLocaleDateString()
                : null,
              color: WARN, icon: UserCheck,
            } : null
          }
        />

        <DomainCard
          title="Reporting"
          icon={ClipboardList}
          testid="brief-reports"
          rows={[
            { label: "Drafts", value: k.reports_drafts, link: "/portal/reports" },
            { label: "In review", value: k.reports_in_review, link: "/portal/reports", alert: k.reports_in_review > 0 },
            { label: "Past period", value: k.reports_past_period, alert: k.reports_past_period > 0 },
            { label: "Signed · 30d", value: k.reports_signed_30d, link: "/portal/reports" },
          ]}
        />
      </div>

      {/* Quick actions dock */}
      <div className="mt-4 panel p-4">
        <div className="label-mono mb-3 flex items-center gap-2" style={{ color: WARN }}>
          <Radio size={12} /> Quick actions
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={onNewDocket} className="btn-cta primary" data-testid="qa-new-docket">
            <Plus size={12} /> New Docket
          </button>
          <Link to="/portal/reports" className="btn-cta" data-testid="qa-new-report">
            <ClipboardList size={12} /> New Report
          </Link>
          <Link to="/portal/hrm" className="btn-cta" data-testid="qa-new-posting">
            <Briefcase size={12} /> Post Job
          </Link>
          <Link to="/portal/funding" className="btn-cta" data-testid="qa-funding">
            <TrendingUp size={12} /> Funding pipeline
          </Link>
          <Link to="/portal/polls" className="btn-cta" data-testid="qa-new-poll">
            <TrendingUp size={12} /> Open Poll
          </Link>
          <Link to="/portal/surveys" className="btn-cta" data-testid="qa-new-survey">
            <Users size={12} /> Launch Survey
          </Link>
          <Link to="/portal/sessions" className="btn-cta" data-testid="qa-new-session">
            <Radio size={12} /> Council Session
          </Link>
          {k.registry_pending > 0 && (
            <Link to="/portal/clerk" className="btn-cta gold" data-testid="qa-registry">
              <IdCard size={12} /> Registry queue ({k.registry_pending})
            </Link>
          )}
        </div>
      </div>

      {/* Escalations feed — past-deadline items across modules */}
      <div className="mt-4 panel p-5" data-testid="escalations-feed">
        <div className="flex items-baseline justify-between mb-3">
          <div className="label-mono flex items-center gap-2" style={{ color: CRIT }}>
            <AlertTriangle size={12} /> Overdue &amp; escalations
          </div>
          <span className="font-mono text-[10px] text-white/40">
            {brief.escalations.length} item{brief.escalations.length === 1 ? "" : "s"} past due
          </span>
        </div>
        {brief.escalations.length === 0 ? (
          <div className="font-mono text-xs text-white/40 py-6 text-center">
            All commitments on schedule. Nothing past-due across any module.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {brief.escalations.map(e => (
              <Link
                key={`${e.module}-${e.id}`}
                to={e.link}
                className="border border-white/10 p-3 hover:border-white/40 transition-colors block"
                data-testid={`escalation-${e.module}-${e.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="tag" style={{ color: severityColor(e.severity) }}>
                    {e.module}
                  </span>
                  <span className="font-mono text-[10px] tabular-nums" style={{ color: severityColor(e.severity) }}>
                    {e.days_overdue}d past due
                  </span>
                </div>
                <div className="font-display text-sm mt-2 leading-tight truncate">{e.title}</div>
                <div className="font-mono text-[10px] text-white/50 mt-1 flex items-center justify-between">
                  <span className="truncate">{e.owner || "—"}</span>
                  <ArrowUpRight size={11} className="text-white/40" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function severityColor(s) {
  return s === "critical" ? CRIT : s === "high" ? CRIT : s === "medium" ? WARN : MUTE;
}

/* Reusable KPI card for one domain (Ops / HR / Registry / Reports) */
function DomainCard({ title, icon: Icon, rows, footer, testid }) {
  return (
    <div className="panel p-5 corner-tick" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="label-mono flex items-center gap-2">
          <Icon size={12} /> {title}
        </div>
      </div>
      <div className="space-y-2">
        {rows.map(r => (
          <RowLine key={r.label} row={r} />
        ))}
      </div>
      {footer && (
        <div className="mt-4 pt-3 border-t border-white/10">
          <div className="flex items-baseline justify-between">
            <span className="label-mono flex items-center gap-1.5">
              {footer.icon && <footer.icon size={11} />} {footer.label}
            </span>
            <span className="font-display text-xl tabular-nums" style={{ color: footer.color || OK }}>
              {footer.value}
            </span>
          </div>
          {footer.pct != null && (
            <div className="h-1 bg-white/5 mt-2">
              <div className="h-full" style={{ width: `${Math.min(100, footer.pct)}%`, background: footer.color }} />
            </div>
          )}
          {footer.subtext && (
            <div className="font-mono text-[10px] text-white/50 mt-1 truncate" title={footer.subtext}>
              {footer.subtext}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RowLine({ row }) {
  const inner = (
    <div className="flex items-baseline justify-between font-mono text-xs">
      <span className="text-white/70">{row.label}</span>
      <span
        className="font-display text-lg tabular-nums"
        style={{ color: row.alert ? WARN : "#FFFFFF" }}
      >
        {row.value ?? 0}
      </span>
    </div>
  );
  if (row.link) {
    return (
      <Link to={row.link} className="block hover:opacity-100 opacity-90" data-testid={`row-link-${row.label.toLowerCase().replace(/\s+/g,'-')}`}>
        {inner}
      </Link>
    );
  }
  return inner;
}
